import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import _ from 'lodash';

import {
    DefaultButton,
    PrimaryButton,
    Panel,
    PanelType,
    Separator,
    Stack
} from '@fluentui/react';

import { RootState } from '../../../../../redux/application/redux-store';
import { Department } from '../../../../../api-client';
import { TRANSLATION_KEYS } from './localization/translation-keys';

import { FormProvider, IAIComboBox, IAITextField } from '../../../../../common/components/IAIForm';

interface FormValuesProps extends Department {
    groupKeys: string[];
}

interface IFormDrawer {
    topic?: Department;
    disabled?: boolean;
    drawerVisible?: boolean;
    onDrawerConfirm: (dataItem: Department) => void;
    onDrawerDismiss: () => void;
}

const FormDrawer: React.FunctionComponent<IFormDrawer> = ({
    topic,
    disabled,
    drawerVisible,
    onDrawerConfirm,
    onDrawerDismiss
}) => {
    const {
        t: translate
    } = useTranslation();

    //#region Form-Configuration

    const formDataSchema = Yup.object().shape({
        displayName: Yup.string().required(translate(TRANSLATION_KEYS.TitleErrorMessage)),
        groupKeys: Yup.array().min(1, translate(TRANSLATION_KEYS.GroupErrorMessage)).required(translate(TRANSLATION_KEYS.GroupErrorMessage)),
        resolutionDays: Yup.number().required(translate(TRANSLATION_KEYS.ResolutionDaysErrorMessage)).min(1, translate(TRANSLATION_KEYS.ResolutionDaysMinErrorMessage)).positive(translate(TRANSLATION_KEYS.ResolutionDaysPositiveErrorMessage)),
    });

    const defaultValues = useMemo(
        () => ({
            displayName: topic?.displayName || '',
            groupKeys: _.map(topic?.groups, entry => entry.key) || [],
            resolutionDays: topic?.resolutionDays || 1,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [topic]
    );

    const methods = useForm<FormValuesProps>({ resolver: yupResolver(formDataSchema), defaultValues });

    const {
        reset,
        handleSubmit,
        formState: { isSubmitting }
    } = methods;

    useEffect(() => {
        reset(defaultValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topic]);

    //#endregion

    const _handleDismiss = () => {
        reset(defaultValues);
        onDrawerDismiss();
    }

    const iaiSystem = useSelector((state: RootState) => state.system);

    const _handleSubmit = (data: FormValuesProps) => {
        let topicData = {
            ...topic,
            ...data,
            groups: _.filter(iaiSystem.groups, entry => _.some(data.groupKeys, key => entry.key === key))
        };

        onDrawerConfirm(topicData);
    }

    const disableCondition = disabled || !iaiSystem.groupsInitialized || isSubmitting;
    const groupOptions = _.map(iaiSystem.groups, entry => ({ key: entry.key!, text: entry.text! }));

    return (
        <FormProvider methods={methods}>
            <Panel
                type={PanelType.medium}
                headerText={translate(TRANSLATION_KEYS.Heading)}
                isOpen={drawerVisible}
                isBlocking={true}
                isFooterAtBottom={true}
                onDismiss={_handleDismiss}
                onRenderFooterContent={() => (
                    <div className="text-end">
                        <DefaultButton
                            disabled={disableCondition}
                            iconProps={{ iconName: 'Cancel' }}
                            styles={{ root: { marginRight: 10 } }}
                            onClick={_handleDismiss}
                        >
                            {translate(TRANSLATION_KEYS.DismissText)}
                        </DefaultButton>
                        <PrimaryButton
                            type="submit"
                            disabled={disableCondition}
                            iconProps={{ iconName: 'CheckMark' }}
                            onClick={handleSubmit(_handleSubmit)}
                        >
                            {translate(TRANSLATION_KEYS.ConfirmText)}
                        </PrimaryButton>
                    </div>
                )}
            >
                <Separator />

                <Stack tokens={{ childrenGap: 15 }}>
                    <IAITextField
                        name="displayName"
                        label={translate(TRANSLATION_KEYS.TitleLabel)}
                        placeholder={translate(TRANSLATION_KEYS.TitlePlaceholder)}
                        required
                        disabled={disableCondition}
                    />

                    <IAIComboBox
                        name="groupKeys"
                        label={translate(TRANSLATION_KEYS.GroupLabel)}
                        placeholder={translate(TRANSLATION_KEYS.GroupPlaceholder)}
                        autoComplete="on"
                        multiSelect
                        required
                        options={groupOptions}
                    />

                    <IAITextField
                        name="resolutionDays"
                        type='number'
                        label={translate(TRANSLATION_KEYS.ResolutionDaysLabel)}
                        placeholder={translate(TRANSLATION_KEYS.ResolutionDaysPlaceholder)}
                        required
                        suffix={translate(TRANSLATION_KEYS.ResolutionDaysSuffixLabel)}
                        disabled={disableCondition} />
                </Stack>
            </Panel>
        </FormProvider>
    );
}

export default FormDrawer;